import { convertStringToJSON } from "@/utils";
import type {
  HealthProfileItem,
  HealthProfileSuccessResponseData,
} from "@/services/core-api-adapter";
import type { MetricItem } from "../../Common/types";
import { WellnessScoreStatus } from "../../Common/types";
import { riskLevel } from "./riskLevels";

export type RiskLevelKey = "healthy" | "needAttention" | "needsSupport";

/** Business rules: Pulse <50 or 61-90 AMBER, 50-60 GREEN, >90 RED */
export function getPulseStatus(value: number): RiskLevelKey {
  if (value > 90) return riskLevel.needsSupport as RiskLevelKey;
  if (value >= 50 && value <= 60) return riskLevel.healthy as RiskLevelKey;
  return riskLevel.needAttention as RiskLevelKey;
}

/** Business rules: Systolic <90 or 121-130 AMBER, 90-120 GREEN, >130 RED */
export function getSystolicStatus(value: number): RiskLevelKey {
  if (value > 130) return riskLevel.needsSupport as RiskLevelKey;
  if (value >= 90 && value <= 120) return riskLevel.healthy as RiskLevelKey;
  return riskLevel.needAttention as RiskLevelKey;
}

/** Business rules: Diastolic <60 or 81-90 AMBER, 60-80 GREEN, >90 RED */
export function getDiastolicStatus(value: number): RiskLevelKey {
  if (value > 90) return riskLevel.needsSupport as RiskLevelKey;
  if (value >= 60 && value <= 80) return riskLevel.healthy as RiskLevelKey;
  return riskLevel.needAttention as RiskLevelKey;
}

/** Business rules: Respiratory <12 or 19-20 AMBER, 12-18 GREEN, >20 RED */
export function getBreathingRateStatus(value: number): RiskLevelKey {
  if (value > 20) return riskLevel.needsSupport as RiskLevelKey;
  if (value >= 12 && value <= 18) return riskLevel.healthy as RiskLevelKey;
  return riskLevel.needAttention as RiskLevelKey;
}

function worstStatus(a: RiskLevelKey, b: RiskLevelKey): RiskLevelKey {
  if (a === riskLevel.needsSupport || b === riskLevel.needsSupport)
    return riskLevel.needsSupport as RiskLevelKey;
  if (a === riskLevel.needAttention || b === riskLevel.needAttention)
    return riskLevel.needAttention as RiskLevelKey;
  return riskLevel.healthy as RiskLevelKey;
}

function statusToWellnessScoreStatus(
  status: RiskLevelKey
): WellnessScoreStatus {
  if (status === riskLevel.needsSupport)
    return WellnessScoreStatus.NeedsSupport;
  if (status === riskLevel.needAttention)
    return WellnessScoreStatus.NeedsAttention;
  return WellnessScoreStatus.Healthy;
}

function getItemByType(
  items: HealthProfileSuccessResponseData["items"],
  questionType: string
): HealthProfileItem | undefined {
  return items.find((i) => i.questionType === questionType);
}

function parseNumber(s: string | undefined | null): number | undefined {
  if (s == null || s === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function formatLastUpdated(iso?: string | null): string | undefined {
  if (!iso) return undefined;
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return undefined;
    return d.toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
}

/** Current blood pressure and metadata from health profile for detail screen (InfoCard + gauge). */
export interface BloodPressureDetail {
  systolic: number;
  diastolic: number;
  status: WellnessScoreStatus;
  lastUpdated?: string;
}

/** Current heart rate and metadata from health profile for detail screen (InfoCard + gauge). */
export interface HeartRateDetail {
  value: number;
  status: WellnessScoreStatus;
  lastUpdated?: string;
}

/** Current breathing rate and metadata from health profile for detail screen (InfoCard). */
export interface BreathingRateDetail {
  value: number;
  status: WellnessScoreStatus;
  lastUpdated?: string;
}

export function getBloodPressureDetailFromProfile(
  healthProfile: HealthProfileSuccessResponseData | null | undefined
): BloodPressureDetail | null {
  if (!healthProfile?.items?.length) return null;
  const items = healthProfile.items;
  const systolic = getItemByType(items, "SystolicBloodPressure");
  const diastolic = getItemByType(items, "DiastolicBloodPressure");
  const sVal = parseNumber(systolic?.data?.value);
  const dVal = parseNumber(diastolic?.data?.value);
  if (sVal == null || dVal == null) return null;
  const statusKey = worstStatus(
    getSystolicStatus(sVal),
    getDiastolicStatus(dVal)
  );
  return {
    systolic: sVal,
    diastolic: dVal,
    status: statusToWellnessScoreStatus(statusKey),
    lastUpdated: formatLastUpdated(healthProfile.lastUpdatedAt),
  };
}

export function getHeartRateDetailFromProfile(
  healthProfile: HealthProfileSuccessResponseData | null | undefined
): HeartRateDetail | null {
  if (!healthProfile?.items?.length) return null;
  const pulse = getItemByType(healthProfile.items, "Pulse");
  const value = parseNumber(pulse?.data?.value);
  if (value == null) return null;
  return {
    value,
    status: statusToWellnessScoreStatus(getPulseStatus(value)),
    lastUpdated: formatLastUpdated(healthProfile.lastUpdatedAt),
  };
}

export function getBreathingRateDetailFromProfile(
  healthProfile: HealthProfileSuccessResponseData | null | undefined
): BreathingRateDetail | null {
  if (!healthProfile?.items?.length) return null;
  const breathing = getItemByType(healthProfile.items, "BreathingRate");
  const value = parseNumber(breathing?.data?.value);
  if (value == null) return null;
  return {
    value,
    status: statusToWellnessScoreStatus(getBreathingRateStatus(value)),
    lastUpdated: formatLastUpdated(healthProfile.lastUpdatedAt),
  };
}

/**
 * Builds the vitals metrics list (BP, heart rate, breathing) from get-health-profile response
 * so the dashboard updates after refetch when the user submits manual entry.
 */
export function getVitalsMetricsFromHealthProfile(
  healthProfile: HealthProfileSuccessResponseData | null
): MetricItem[] {
  if (!healthProfile) return [];
  const items = healthProfile?.items ?? [];
  const systolic = getItemByType(items, "SystolicBloodPressure");
  const diastolic = getItemByType(items, "DiastolicBloodPressure");
  const pulse = getItemByType(items, "Pulse");
  const breathing = getItemByType(items, "BreathingRate");

  const bpValue =
    systolic?.data?.value && diastolic?.data?.value
      ? `${systolic.data.value}/${diastolic.data.value}`
      : "—";
  const hrValue = pulse?.data?.value ?? "—";
  const breathingValue = breathing?.data?.value ?? "—";

  const sVal = parseNumber(systolic?.data?.value);
  const dVal = parseNumber(diastolic?.data?.value);
  const pulseVal = parseNumber(pulse?.data?.value);
  const breathingVal = parseNumber(breathing?.data?.value);

  const bpStatus =
    sVal != null && dVal != null
      ? worstStatus(getSystolicStatus(sVal), getDiastolicStatus(dVal))
      : riskLevel.healthy;
  const hrStatus =
    pulseVal != null ? getPulseStatus(pulseVal) : riskLevel.healthy;
  const breathingStatus =
    breathingVal != null
      ? getBreathingRateStatus(breathingVal)
      : riskLevel.healthy;

  return [
    {
      id: "bp",
      title: "Blood pressure",
      value: bpValue,
      status: bpStatus,
      showChevron: true,
    },
    {
      id: "hr",
      title: "Heart rate (beats/min)",
      value: hrValue,
      status: hrStatus,
      showChevron: true,
    },
    {
      id: "breathing",
      title: "Breathing (breaths/min)",
      value: breathingValue,
      status: breathingStatus,
      showChevron: true,
    },
  ];
}
